// Abertura animada das telas de números. No Painel roda UMA vez por carga do
// app — na prática, logo depois do login; no Meu desempenho, a cada entrada. Os blocos sobem em cascata, os números contam
// a partir do zero e os gráficos se desenham (linha varrendo da esquerda,
// barras crescendo do eixo). Voltar ao Painel depois disso cai na entrada
// discreta de sempre (REVEAL): a cena é boas-vindas, não pedágio.
//
// Por que CSS e não a animação do recharts: ver o topo de dashboard-charts.tsx.
// Animação CSS pausada em aba de fundo retoma quando a aba volta; a do recharts
// fica presa no primeiro quadro. Mesmo assim, aba escondida no mount não ganha
// abertura — ninguém veria.
import { createContext, useContext, useEffect, useRef, useState, type CSSProperties } from "react";
import { animate } from "motion/react";

// Depois disso, bloco ou gráfico que só montar agora (consulta lenta) entra
// pronto: uma peça se animando sozinha, segundos depois, parece falha.
const INTRO_WINDOW_MS = 4_000;
// Degrau da cascata entre um bloco e o seguinte.
const INTRO_STEP_MS = 90;

let introStartedAt: number | null = null;
// Marcado só no commit: um render que suspende (tradução, chunk lazy) é
// descartado e remontado pelo React, e não pode gastar a abertura.
let introPlayed = false;

function motionAllowed(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  if (document.visibilityState === "hidden") return false;
  return window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
}

// Chamado no mount da tela. "once": só na primeira vez desta carga do app (o
// Painel pós-login); "every": toda vez que a tela monta.
export function useIntroPlayback(mode: "once" | "every" = "once"): boolean {
  const [intro] = useState(() => {
    if ((mode === "once" && introPlayed) || !motionAllowed()) return false;
    introStartedAt = performance.now();
    return true;
  });
  useEffect(() => {
    if (intro && mode === "once") introPlayed = true;
  }, [intro, mode]);
  return intro;
}

const IntroContext = createContext(false);
export const IntroProvider = IntroContext.Provider;

// Para quem monta DEPOIS do Painel (gráfico lazy, número que chegou da API):
// ainda dá tempo de entrar na cena? Decidido no mount e congelado — um refetch
// não faz o bloco se animar de novo.
export function useIntroActive(): boolean {
  const intro = useContext(IntroContext);
  const [active] = useState(
    () => intro && introStartedAt != null && performance.now() - introStartedAt < INTRO_WINDOW_MS,
  );
  return active;
}

// O atraso também vai como --intro-delay: o que mora dentro do bloco (gráfico,
// barras) entra depois do próprio bloco, e não junto.
export function introStep(step: number): CSSProperties {
  const delay = `${step * INTRO_STEP_MS}ms`;
  return { animationDelay: delay, ["--intro-delay" as string]: delay };
}

// "128", "45%", "3/12" → partes numéricas para contar; o resto ("—", "1:02")
// não conta, só aparece.
const COUNTABLE = /^(\d+)(%?)(?:\/(\d+))?$/;

// Número que conta de zero até o valor na abertura. Escreve direto no nó (sem
// re-render por quadro); o leitor de tela lê só o valor final, em sr-only.
export function CountUp({ value, className }: { value: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const match = COUNTABLE.exec(value);

  useEffect(() => {
    const node = ref.current;
    if (!node || !match) return;
    const target = Number(match[1]);
    const suffix = match[2] ?? "";
    const denominator = match[3] != null ? `/${match[3]}` : "";
    const controls = animate(0, target, {
      duration: Math.min(1.6, 0.8 + target / 400),
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => {
        node.textContent = `${Math.round(v)}${suffix}${denominator}`;
      },
    });
    return () => controls.stop();
    // Só no mount: a troca de valor remonta o componente (key no MetricValue).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!match) return <span className={className}>{value}</span>;
  return (
    <span className={className}>
      <span ref={ref} aria-hidden="true">
        {`0${match[2] ?? ""}${match[3] != null ? `/${match[3]}` : ""}`}
      </span>
      <span className="sr-only">{value}</span>
    </span>
  );
}
